import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout } from '@/components/Layout';
import { Button, FieldRow, ThinkingState } from '@/components/ui';
import { Icon } from '@/components/kol/Icon';
import {
  ApiError,
  confirmDocument,
  createVehicle,
  getDocument,
  type CarnafDocumentType,
  type DocumentDetail,
} from '@/lib/api';

/**
 * OCR review screen — eng plan §10 "the aha moment".
 *
 * Polls /api/documents/:id every 2 seconds until OCR completes. Surfaces
 * the extracted fields with the per-field confidence pulled from the
 * Cloud Function callback. Owner edits → confirms → vehicle is created
 * from the (possibly-edited) extracted data and the route navigates to
 * the new vehicle detail.
 *
 * Polls in: PENDING / PROCESSING.
 * Branches on: COMPLETED → review UI; FAILED → retry-upload prompt;
 *              MANUAL_REVIEW → review UI with low-confidence callouts;
 *              INFECTED virus scan → permanent reject.
 */

const POLL_MS = 2000;
const MAX_POLLS = 60; // ~2 minutes ceiling; the Cloud Function should finish well under that

// Field keys match the canonical RawExtractedFields shape that field-extractor.ts
// consumes — so the OCR pipeline (Claude Vision or, eventually, Document AI)
// can emit one consistent shape that both this review screen and the KB
// hydrator read from without aliases.
type FieldKey = 'vin' | 'plate' | 'make' | 'model' | 'year' | 'fuel_type' | 'insured_name';

const FIELD_ORDER: FieldKey[] = ['vin', 'plate', 'make', 'model', 'year', 'fuel_type', 'insured_name'];

export function OcrReviewPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  // When the upload came from a wallet slot on an existing vehicle, the upload
  // page passes ?vehicleId=… through to here so the confirm step can attach
  // the doc to that vehicle instead of (incorrectly) creating a new one from
  // the insurance/service-contract extraction.
  const vehicleIdFromUrl = params.get('vehicleId');
  // Launched from a קוֹל surface → return into /kol/* after confirm/close.
  const kol = params.get('kol') === '1';
  const navigate = useNavigate();
  const [doc, setDoc] = useState<DocumentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<FieldKey, string>>({} as Record<FieldKey, string>);
  const [pending, setPending] = useState(false);
  // Local override when the user corrects the auto-classified type. Sent to
  // /confirm so the backend re-runs applyExtraction with the new type.
  const [typeOverride, setTypeOverride] = useState<CarnafDocumentType | null>(null);
  const [pickingType, setPickingType] = useState(false);

  // Initial extracted fields, prefer extractedData → fall back to edit-state.
  useEffect(() => {
    if (!doc?.extractedData) return;
    const next: Partial<Record<FieldKey, string>> = {};
    for (const key of FIELD_ORDER) {
      const raw = (doc.extractedData as Record<string, unknown>)[key];
      if (raw !== undefined && raw !== null) next[key] = String(raw);
    }
    setEdits((prev) => ({ ...next, ...prev }) as Record<FieldKey, string>);
  }, [doc?.extractedData]);

  // Poll until OCR completes or errors.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function tick() {
      attempts += 1;
      try {
        const d = await getDocument(id!);
        if (cancelled) return;
        setDoc(d);
        if (d.virusScanStatus === 'INFECTED' || d.virusScanStatus === 'SCAN_FAILED') {
          return; // permanent reject; don't keep polling
        }
        if (d.ocrStatus === 'PENDING' || d.ocrStatus === 'PROCESSING') {
          if (attempts < MAX_POLLS) {
            timer = setTimeout(tick, POLL_MS);
          }
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : t('common.error'));
      }
    }

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [id, t]);

  const status: 'waiting' | 'ready' | 'manual' | 'failed' | 'infected' = useMemo(() => {
    if (!doc) return 'waiting';
    if (doc.virusScanStatus === 'INFECTED') return 'infected';
    if (doc.ocrStatus === 'FAILED') return 'failed';
    if (doc.ocrStatus === 'COMPLETED') return 'ready';
    if (doc.ocrStatus === 'MANUAL_REVIEW') return 'manual';
    return 'waiting';
  }, [doc]);

  async function onConfirm() {
    if (!doc) return;
    setError(null);
    setPending(true);
    try {
      // Two confirm paths:
      //  1. Upload came from a wallet slot on an existing vehicle (insurance,
      //     service contract, etc.) → bind the doc to that vehicle and go back.
      //     No createVehicle — the vehicle already exists.
      //  2. Cold upload (first vehicle license) → create the vehicle from the
      //     extracted fields, bind the doc, navigate to the new detail page.
      if (vehicleIdFromUrl) {
        await confirmDocument(
          doc.id,
          edits as unknown as Record<string, unknown>,
          vehicleIdFromUrl,
          typeOverride ?? undefined,
        );
        navigate(kol ? `/kol/vehicles/${vehicleIdFromUrl}/documents` : `/vehicles/${vehicleIdFromUrl}`, {
          replace: true,
        });
        return;
      }

      // Cold-upload path: build the vehicle from the (possibly-edited) fields.
      const vin = (edits.vin ?? '').trim();
      const year = Number(edits.year ?? 0);
      const make = (edits.make ?? '').trim().toLowerCase();
      const model = (edits.model ?? '').trim().toLowerCase();
      const plate = edits.plate?.trim() || undefined;

      if (!vin || !year || !make || !model) {
        setPending(false);
        setError('Please fill VIN, year, make, and model before continuing.');
        return;
      }

      const vehicle = await createVehicle({
        vin,
        licensePlate: plate,
        oem: make,
        model,
        year,
        sourceDocumentId: doc.id,
      });

      // Bind the doc to the just-created vehicle so applyExtraction can write
      // KB entries on the confirm-side re-run.
      await confirmDocument(doc.id, edits as unknown as Record<string, unknown>, vehicle.id, typeOverride ?? undefined);

      navigate(kol ? `/kol/vehicles/${vehicle.id}` : `/vehicles/${vehicle.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setPending(false);
    }
  }

  return (
    <Layout
      title={t('upload.review_title')}
      trailing={
        <Link
          to={kol ? '/kol' : '/'}
          aria-label={t('common.close', { defaultValue: 'סגירה' })}
          className="w-10 h-10 rounded-full border border-carnaf-line bg-carnaf-paper-elevated grid place-items-center text-carnaf-ink-muted shadow-kol-1"
        >
          <Icon name="close" size={19} />
        </Link>
      }
    >
      {status === 'waiting' && (
        <ThinkingState
          status={[
            t('upload.reading_doc', { defaultValue: 'קוראים את המסמך…' }),
            t('upload.checking_dates', { defaultValue: 'בודקים תאריכים…' }),
            t('upload.almost_done', { defaultValue: 'כמעט סיימנו…' }),
          ]}
        />
      )}

      {status === 'failed' && (
        <Banner tone="warning">
          {t('upload.failed_msg', {
            defaultValue: 'לא הצלחנו לקרוא את המסמך. צילום מחדש בתאורה טובה יותר, או הזנה ידנית למטה.',
          })}
        </Banner>
      )}

      {status === 'infected' && (
        <Banner tone="danger">
          {t('upload.infected_msg', {
            defaultValue: 'הקובץ לא עבר את סריקת האבטחה. הוא נדחה ולא נשמר. אנא העלו קובץ אחר.',
          })}
        </Banner>
      )}

      {(status === 'ready' || status === 'manual' || status === 'failed') && doc && (
        <div className="space-y-3 pb-32">
          {status === 'manual' && (
            <Banner tone="warning">
              {t('upload.manual_review_msg', {
                defaultValue: 'חלק מהשדות בבטחון נמוך. בדקו ותקנו לפני אישור.',
              })}
            </Banner>
          )}

          {/* Detected-type pill. Only shown when the auto-classifier set the
              type from Vision (extractedData carries the _detectedType marker)
              or when the user has chosen an override. */}
          {(detectedType(doc) || typeOverride) && (
            <TypePill
              currentType={typeOverride ?? doc.documentType}
              detectionConfidence={detectedConfidence(doc)}
              onOpenPicker={() => setPickingType(true)}
              t={t}
            />
          )}

          {pickingType && (
            <TypePicker
              current={typeOverride ?? doc.documentType}
              onPick={(picked) => {
                setTypeOverride(picked);
                setPickingType(false);
              }}
              onCancel={() => setPickingType(false)}
              t={t}
            />
          )}

          {FIELD_ORDER.map((key) => {
            const conf = doc.ocrPerFieldConf?.[key] ?? null;
            const lowConf = conf !== null && conf < 0.85;
            const tabular = key === 'vin' || key === 'plate' || key === 'year';
            return (
              <FieldRow
                key={key}
                label={prettyLabel(key, t)}
                value={edits[key] ?? ''}
                onChange={(v) => setEdits((prev) => ({ ...prev, [key]: v }))}
                lowConfidence={lowConf}
                tabular={tabular}
              />
            );
          })}

          {error && (
            <p role="alert" className="text-body-sm text-carnaf-red">
              {error}
            </p>
          )}

          {/* Sticky bottom CTA */}
          <div
            className="fixed bottom-0 inset-x-0 bg-white border-t border-carnaf-line px-5 py-4"
            style={{
              paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
              boxShadow: '0 -1px 0 0 #E0E8E1, 0 -8px 24px -8px rgba(26, 30, 29, 0.06)',
            }}
          >
            <Button variant="primary" size="md" fullWidth disabled={pending} onClick={onConfirm}>
              {pending ? t('common.loading') : t('upload.confirm')}
            </Button>
          </div>
        </div>
      )}
    </Layout>
  );
}

// Doc-types the user can switch between via the picker. INSURANCE_POLICY
// (legacy) and OTHER are intentionally excluded from the menu — the user
// shouldn't actively pick them.
const PICKABLE_TYPES: CarnafDocumentType[] = [
  'VEHICLE_REGISTRATION',
  'DRIVER_LICENSE',
  'INSURANCE_COMPULSORY',
  'INSURANCE_COMPREHENSIVE',
  'SERVICE_CONTRACT',
];

function typeLabel(type: CarnafDocumentType, t: (k: string, o?: Record<string, unknown>) => string): string {
  switch (type) {
    case 'VEHICLE_REGISTRATION':
      return t('doc_types.vehicle_registration', { defaultValue: 'רישיון רכב' });
    case 'DRIVER_LICENSE':
      return t('doc_types.driver_license', { defaultValue: 'רישיון נהיגה' });
    case 'INSURANCE_COMPULSORY':
      return t('doc_types.insurance_compulsory', { defaultValue: 'ביטוח חובה' });
    case 'INSURANCE_COMPREHENSIVE':
      return t('doc_types.insurance_comprehensive', { defaultValue: 'ביטוח מקיף' });
    case 'SERVICE_CONTRACT':
      return t('doc_types.service_contract', { defaultValue: 'כתב שירות' });
    case 'INSURANCE_POLICY':
      return t('doc_types.insurance_policy', { defaultValue: 'פוליסת ביטוח' });
    case 'OTHER':
      return t('doc_types.other', { defaultValue: 'מסמך אחר' });
  }
}

/** Tries to read the auto-classifier marker stored alongside extractedData. */
function detectedType(doc: DocumentDetail): CarnafDocumentType | null {
  if (!doc.extractedData || typeof doc.extractedData !== 'object') return null;
  const obj = doc.extractedData as Record<string, unknown>;
  const raw = obj._detectedType;
  return typeof raw === 'string' ? (raw as CarnafDocumentType) : null;
}

function detectedConfidence(doc: DocumentDetail): number | null {
  if (!doc.extractedData || typeof doc.extractedData !== 'object') return null;
  const obj = doc.extractedData as Record<string, unknown>;
  const raw = obj._detectionConfidence;
  return typeof raw === 'number' ? raw : null;
}

function TypePill({
  currentType,
  detectionConfidence,
  onOpenPicker,
  t,
}: {
  currentType: CarnafDocumentType;
  detectionConfidence: number | null;
  onOpenPicker: () => void;
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  const lowConf = detectionConfidence !== null && detectionConfidence < 0.8;
  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-md border p-3 ${
        lowConf ? 'border-carnaf-amber bg-carnaf-amber-soft' : 'border-carnaf-teal/30 bg-carnaf-teal-soft'
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="text-caption text-carnaf-ink-muted">{t('upload.detected_as', { defaultValue: 'זוהה כ:' })}</div>
        <div className="text-body font-semibold text-carnaf-ink truncate">{typeLabel(currentType, t)}</div>
        {lowConf && (
          <div className="text-caption text-carnaf-amber mt-0.5">
            {t('upload.low_confidence_detection', { defaultValue: 'בטחון נמוך — בדקו שהזיהוי נכון.' })}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onOpenPicker}
        className="text-body-sm text-carnaf-teal-pressed font-medium underline-offset-2 hover:underline whitespace-nowrap"
      >
        {t('upload.change_type', { defaultValue: 'שינוי' })}
      </button>
    </div>
  );
}

function TypePicker({
  current,
  onPick,
  onCancel,
  t,
}: {
  current: CarnafDocumentType;
  onPick: (type: CarnafDocumentType) => void;
  onCancel: () => void;
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Match the app's other dialogs (AccountSheet): Escape closes, initial focus
  // moves into the dialog so keyboard users land here when it opens.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    dialogRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label={t('upload.pick_type', { defaultValue: 'בחירת סוג המסמך' })}
      className="rounded-md border border-carnaf-line bg-white p-3 space-y-2 focus:outline-none"
    >
      <div className="text-body-sm text-carnaf-ink-muted mb-2">
        {t('upload.pick_type', { defaultValue: 'איזה סוג מסמך זה?' })}
      </div>
      <div className="grid grid-cols-1 gap-1.5">
        {PICKABLE_TYPES.map((type) => {
          const isCurrent = type === current;
          return (
            <button
              key={type}
              type="button"
              onClick={() => onPick(type)}
              className={`text-start rounded-md border px-3 py-2 text-body-sm transition-colors ${
                isCurrent
                  ? 'border-carnaf-teal bg-carnaf-teal-soft text-carnaf-ink font-semibold'
                  : 'border-carnaf-line hover:border-carnaf-teal hover:bg-carnaf-teal-soft'
              }`}
            >
              {typeLabel(type, t)}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={onCancel}
        className="text-body-sm text-carnaf-ink-muted underline-offset-2 hover:underline"
      >
        {t('common.cancel', { defaultValue: 'ביטול' })}
      </button>
    </div>
  );
}

function Banner({ tone, children }: { tone: 'warning' | 'danger'; children: React.ReactNode }) {
  const cls =
    tone === 'danger'
      ? 'bg-carnaf-red-soft border border-carnaf-red text-carnaf-red'
      : 'bg-carnaf-amber-soft border border-carnaf-amber text-carnaf-amber';
  return (
    <div className={`rounded-md p-4 text-body-sm ${cls}`} role="alert">
      {children}
    </div>
  );
}

function prettyLabel(key: FieldKey, t: (k: string, o?: Record<string, unknown>) => string): string {
  // Use translation keys with English fallbacks
  switch (key) {
    case 'vin':
      return t('ocr_fields.vin', { defaultValue: 'מספר שלדה (VIN)' });
    case 'plate':
      return t('ocr_fields.plate', { defaultValue: 'לוחית רישוי' });
    case 'make':
      return t('ocr_fields.make', { defaultValue: 'יצרן' });
    case 'model':
      return t('ocr_fields.model', { defaultValue: 'דגם' });
    case 'year':
      return t('ocr_fields.year', { defaultValue: 'שנת ייצור' });
    case 'fuel_type':
      return t('ocr_fields.fuel_type', { defaultValue: 'סוג דלק' });
    case 'insured_name':
      return t('ocr_fields.insured_name', { defaultValue: 'שם הבעלים' });
  }
}
